/**
 * Trip Atlas — every visit in the PazNet axon drawing language.
 *
 *   /trips/atlas              the portfolio: one lane per property on one timeline
 *   /trips/atlas/:locationId  one property: one lane per year, Jan → Dec, so the
 *                             years read against each other, with what the
 *                             property brought in and what it cost
 *
 * Both share the period (all trips / a year / a quarter), the colour mode
 * (state / length / revenue / margin) and the search. Period, colour and search
 * live in the URL, so any view can be shared as a link.
 *
 * Money comes only from the Zoho CRM Deals linked to each trip
 * (`trip_financials`); Zoho is master and is only read. A cost Zoho does not
 * record is shown as not recorded, never as zero.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Breakdown,
  CalloutLayer,
  ColorByControl,
  ColorLegend,
  LongTag,
  MAX_CALLOUTS,
  PeriodBar,
  SummaryCards,
  Swatch,
  bandRows,
  money,
  pct,
  type AnchorSink,
} from '../components/trips/AtlasControls';
import { TripAtlas3D, type CalloutAnchor } from '../components/trips/TripAtlas3D';
import { useTripBillingOverview } from '../hooks/useTripBilling';
import { useAtlasExtras, useTripRuns } from '../hooks/useTripContinuity';
import { formatDate } from '../lib/formatters';
import {
  LONG_TRIP_DAYS,
  TRIP_STATES,
  TRIP_STATE_ORDER,
  adjacentVisit,
  atlasYears,
  buildTripAtlas,
  colorLegend,
  formatPeriod,
  parsePeriod,
  periodLabel,
  quarterSummaries,
  stepInList,
  summarizeTrips,
  tripInPeriod,
  tripMargin,
  yearSummaries,
  type AtlasPeriod,
  type AtlasTripInput,
  type ColorBy,
} from '../lib/tripAtlas';
import { localToday } from '../lib/tripLifecycle';
import { QUICK_FILTERS, STATE_QUERY_WORD, buildSearchIndex, queryHasWord, runTripQuery, toggleQueryWord } from '../lib/tripQuery';

/** Typing highlights at once; the camera and the URL follow once typing pauses. */
const SETTLE_MS = 260;
const COLOR_MODES: ColorBy[] = ['state', 'length', 'revenue', 'margin'];

function isTypingTarget(el: EventTarget | null) {
  const t = el as HTMLElement | null;
  return Boolean(t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable));
}

export function TripAtlasPage() {
  const { locationId } = useParams<{ locationId?: string }>();
  // A fresh view per property: selection, camera and search start clean.
  return <TripAtlasView key={locationId ?? 'all'} locationId={locationId ?? null} />;
}

function TripAtlasView({ locationId }: { locationId: string | null }) {
  const tripsQ = useTripBillingOverview();
  const runsQ = useTripRuns();
  const extrasQ = useAtlasExtras();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [selected, setSelected] = useState<string | null>(params.get('trip'));
  const period = useMemo(() => parsePeriod(params.get('period')), [params]);
  const colorParam = params.get('color') as ColorBy | null;
  // Length is the default: it reads at a glance, and long trips stand out.
  const colorBy: ColorBy = colorParam && COLOR_MODES.includes(colorParam) ? colorParam : 'length';
  // What the camera frames on the next nonce bump (empty = the whole atlas).
  const [frame, setFrame] = useState<{ nonce: number; ids: string[] }>({ nonce: 0, ids: [] });
  const [listHover, setListHover] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef(new Map<string, HTMLLIElement>());
  const anchorSink = useRef<AnchorSink>(() => {});
  const onAnchors = useCallback((a: CalloutAnchor[]) => anchorSink.current(a), []);
  const frameIds = useCallback((ids: string[]) => setFrame((f) => ({ nonce: f.nonce + 1, ids })), []);

  const extras = extrasQ.data;
  const runs = useMemo(() => runsQ.data ?? [], [runsQ.data]);
  // Trips with their Zoho money attached; one property's trips on its own atlas.
  const trips = useMemo(() => {
    const all = (tripsQ.data ?? []) as unknown as AtlasTripInput[];
    const scoped = locationId ? all.filter((t) => t.location_id === locationId) : all;
    return scoped.map((t) => ({ ...t, finance: extras?.financeByTrip.get(t.trip_id) ?? null }));
  }, [tripsQ.data, extras, locationId]);
  const property = locationId ? trips[0] ?? null : null;

  const atlas = useMemo(
    () =>
      buildTripAtlas({
        trips,
        runs: runs.map((r) => ({ run_id: r.run_id, run_number: r.run_number, title: r.title, stops: r.stops.map((s) => ({ trip_id: s.trip_id, stop: s.stop, status: s.status })) })),
        openItemsByLocation: locationId
          ? new Map([[locationId, extras?.openByLocation.get(locationId) ?? 0]])
          : extras?.openByLocation ?? new Map(),
        today: localToday(),
        laneBy: locationId ? 'year' : 'property',
      }),
    [trips, runs, extras, locationId],
  );

  const index = useMemo(() => {
    const runNumberByTrip = new Map<string, string>();
    for (const r of runs) for (const s of r.stops) runNumberByTrip.set(s.trip_id, r.run_number);
    return buildSearchIndex(trips, extras?.dealLabels ?? new Map(), runNumberByTrip);
  }, [trips, runs, extras]);

  const result = useMemo(() => runTripQuery(atlas, index, query), [atlas, index, query]);

  // What is visible: the search AND the period. null = everything.
  const periodIds = useMemo(
    () => (period.kind === 'all' ? null : new Set(trips.filter((t) => tripInPeriod(t, period)).map((t) => t.trip_id))),
    [trips, period],
  );
  const visible = useMemo(() => {
    if (!result.tripIds && !periodIds) return null;
    if (!result.tripIds) return periodIds;
    if (!periodIds) return result.tripIds;
    return new Set([...result.tripIds].filter((id) => periodIds.has(id)));
  }, [result, periodIds]);
  const visibleTrips = useMemo(() => (visible ? trips.filter((t) => visible.has(t.trip_id)) : trips), [trips, visible]);
  const summary = useMemo(() => summarizeTrips(visibleTrips), [visibleTrips]);
  const hits = useMemo(
    () => [...visibleTrips].filter((t) => t.start_date).sort((a, b) => (b.start_date ?? '').localeCompare(a.start_date ?? '')),
    [visibleTrips],
  );
  const filtered = visible !== null;

  // A shared link opens straight to its trip or period once the data is in.
  const linkFocused = useRef(false);
  useEffect(() => {
    if (linkFocused.current || !atlas.blocks.length) return;
    linkFocused.current = true;
    if (!selected && result.focusTripId) setSelected(result.focusTripId);
    if (visible) frameIds([...visible]);
    // Once, when the data first arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atlas]);

  const calloutIds = useMemo(() => {
    const ids = selected ? [selected] : [];
    if (filtered) {
      for (const h of hits) {
        if (ids.length >= MAX_CALLOUTS) break;
        if (!ids.includes(h.trip_id)) ids.push(h.trip_id);
      }
    }
    return ids;
  }, [selected, hits, filtered]);

  // URL is written through a ref so callbacks stay stable.
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const writeUrl = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(paramsRef.current);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined) continue;
        if (v) next.set(k, v);
        else next.delete(k);
      }
      paramsRef.current = next;
      setParams(next, { replace: true });
    },
    [setParams],
  );

  /** Trips a search result and a period both admit (empty = frame everything). */
  const idsFor = (r: { tripIds: Set<string> | null }, p: AtlasPeriod) => {
    if (r.tripIds) return [...r.tripIds].filter((id) => p.kind === 'all' || tripInPeriod(atlas.byId.get(id)!, p));
    return p.kind === 'all' ? [] : trips.filter((t) => tripInPeriod(t, p)).map((t) => t.trip_id);
  };

  /**
   * Run a search. The highlight is immediate; the camera flight and the URL
   * wait until typing settles (`settle: false` from chips and the legend
   * frames at once).
   */
  const settleTimer = useRef<number | undefined>(undefined);
  const run = (q: string, settle = false) => {
    setQuery(q);
    const r = runTripQuery(atlas, index, q);
    let sel = selected;
    if (r.focusTripId) sel = r.focusTripId;
    else if (selected && r.tripIds && !r.tripIds.has(selected)) sel = null; // a search that leaves the selection out clears it
    if (sel !== selected) setSelected(sel);
    window.clearTimeout(settleTimer.current);
    const commit = () => {
      writeUrl({ q: q || null, trip: sel });
      frameIds(r.focusTripId ? [r.focusTripId] : idsFor(r, period));
    };
    if (settle) settleTimer.current = window.setTimeout(commit, SETTLE_MS);
    else commit();
  };
  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  const setPeriod = (p: AtlasPeriod) => {
    const dropSel = Boolean(selected && p.kind !== 'all' && !tripInPeriod(atlas.byId.get(selected!) ?? { start_date: null }, p));
    if (dropSel) setSelected(null);
    writeUrl({ period: p.kind === 'all' ? null : formatPeriod(p), ...(dropSel ? { trip: null } : {}) });
    frameIds(idsFor(result, p));
  };
  const setColor = (c: ColorBy) => writeUrl({ color: c === 'length' ? null : c });

  const select = useCallback(
    (id: string | null, fly = false) => {
      setSelected(id);
      writeUrl({ trip: id });
      if (fly && id) frameIds([id]);
    },
    [writeUrl, frameIds],
  );
  const selectFromScene = useCallback((id: string | null) => select(id), [select]);

  // A lane label: the portfolio opens that property's atlas; a property's
  // atlas shows only that year (click again for all trips).
  const periodRef = useRef(period);
  periodRef.current = period;
  const onLane = useCallback(
    (laneKey: string) => {
      if (!locationId) {
        navigate(`/trips/atlas/${laneKey}${colorParam ? `?color=${colorParam}` : ''}`);
        return;
      }
      const y = Number(laneKey);
      const p = periodRef.current;
      const next: AtlasPeriod = p.kind === 'year' && p.year === y ? { kind: 'all' } : { kind: 'year', year: y };
      writeUrl({ period: next.kind === 'all' ? null : formatPeriod(next) });
      frameIds(next.kind === 'all' ? [] : trips.filter((t) => tripInPeriod(t, next)).map((t) => t.trip_id));
    },
    [locationId, navigate, colorParam, writeUrl, frameIds, trips],
  );

  // ↑/↓ walks the visible trips newest first. ←/→ is the same property's
  // previous / next visit: along the lane on the portfolio, by date here.
  const browseIds = useMemo(() => hits.map((t) => t.trip_id), [hits]);
  const chronological = useMemo(
    () => [...trips].filter((t) => t.start_date && t.trip_status !== 'cancelled').sort((a, b) => a.start_date!.localeCompare(b.start_date!)).map((t) => t.trip_id),
    [trips],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const typing = isTypingTarget(e.target);
      if (e.key === 'Escape') {
        if (typing && query) run('');
        else if (selected) select(null);
        else if (typing) (e.target as HTMLElement).blur();
        return;
      }
      if (typing) {
        if (e.key === 'ArrowDown' && e.target === searchRef.current && browseIds.length) {
          e.preventDefault();
          searchRef.current?.blur();
          select(stepInList(browseIds, selected, 1), true);
        }
        return;
      }
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && selected) {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const next = locationId ? stepInList(chronological, selected, dir) : adjacentVisit(atlas, selected, dir);
        if (next && next !== selected) select(next, true);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!browseIds.length) return;
        e.preventDefault();
        select(stepInList(browseIds, selected, e.key === 'ArrowDown' ? 1 : -1), true);
      } else if (e.key === 'f' || e.key === 'F') {
        frameIds(selected && !visible ? [selected] : visible ? [...visible] : []);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // run is recreated each render; the effect re-binds with the state it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atlas, browseIds, chronological, selected, query, visible, select, frameIds, locationId]);

  // Keep the selected row in view as the keyboard walks the list.
  useEffect(() => {
    if (selected) rowRefs.current.get(selected)?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const years = useMemo(() => atlasYears(atlas), [atlas]);
  const total = atlas.blocks.length;
  const loading = tripsQ.isLoading || runsQ.isLoading || extrasQ.isLoading;
  const error = tripsQ.isError || runsQ.isError || extrasQ.isError;
  const asOf = extras?.fetchedAt ? new Date(extras.fetchedAt) : null;
  const sel = selected ? atlas.byId.get(selected) : null;
  const selBlock = selected ? atlas.blockById.get(selected) : null;
  const selRun = selBlock?.runId ? runs.find((r) => r.run_id === selBlock.runId) : null;
  const selDeals = sel ? extras?.dealsByTrip.get(sel.trip_id) ?? [] : [];
  const openHere = sel ? extras?.openByLocation.get(sel.location_id) ?? 0 : 0;

  // Legend rows for the colour mode, counted over what is visible.
  const legendRows = useMemo(() => {
    if (colorBy === 'state') {
      const counts = new Map<string, number>();
      for (const b of atlas.blocks) if (!visible || visible.has(b.tripId)) counts.set(b.state, (counts.get(b.state) ?? 0) + 1);
      return TRIP_STATE_ORDER.map((s) => ({ key: s, label: TRIP_STATES[s].label, count: counts.get(s) ?? 0, filterWord: STATE_QUERY_WORD[s] }));
    }
    return bandRows(colorBy, colorLegend(colorBy, atlas, visible));
  }, [colorBy, atlas, visible]);

  // Money by period: years on "all trips", quarters within a year or a quarter.
  const breakdown = useMemo(() => {
    const scoped = result.tripIds ? trips.filter((t) => result.tripIds!.has(t.trip_id)) : trips;
    if (period.kind === 'all') {
      return {
        title: 'By year — click to open a year',
        active: null as string | null,
        rows: yearSummaries(scoped).map((y) => ({ key: String(y.year), label: String(y.year), summary: y.summary, period: { kind: 'year', year: y.year } as AtlasPeriod })),
      };
    }
    return {
      title: `${period.year} by quarter`,
      active: period.kind === 'quarter' ? `q${period.quarter}` : null,
      rows: quarterSummaries(scoped, period.year).map((q) => ({
        key: `q${q.quarter}`,
        label: `Q${q.quarter}`,
        summary: q.summary,
        period: (period.kind === 'quarter' && period.quarter === q.quarter
          ? { kind: 'year', year: period.year }
          : { kind: 'quarter', year: period.year, quarter: q.quarter }) as AtlasPeriod,
      })),
    };
  }, [trips, result, period]);

  // Portfolio list: each property with its visible count and revenue.
  const propertyRows = useMemo(() => {
    if (locationId) return [];
    return atlas.lanes.map((l) => {
      const s = summarizeTrips(visibleTrips.filter((t) => t.location_id === l.locationId));
      return { ...l, visibleCount: s.trips, revenue: s.revenue, longTrips: s.longTrips };
    });
  }, [atlas, visibleTrips, locationId]);

  const title = locationId ? property?.location_name ?? 'Property' : 'Trip Atlas';
  const countable = trips.filter((t) => t.trip_status !== 'cancelled').length;
  const visibleLabel = `${summary.trips} of ${countable} trips visible · ${periodLabel(period)}`;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          {locationId && (
            <Link to={`/trips/atlas${colorParam ? `?color=${colorParam}` : ''}`} className="text-xs text-text-muted hover:text-text hover:underline">
              ← All properties
            </Link>
          )}
          <h1 className="truncate text-xl font-bold text-text sm:text-2xl">{title}</h1>
          <p className="mt-0.5 text-sm text-text-muted">
            {locationId
              ? `${property?.customer_name && property.customer_name !== property.location_name ? `${property.customer_name} · ` : ''}Trip atlas — one lane per year, January to December. Height is days on site.`
              : 'Every visit, by property and date, in the PazNet axon drawing language. Click a property to open its own atlas.'}
          </p>
        </div>
        <p className="text-sm font-semibold tabular-nums text-text" role="status" aria-live="polite">{loading ? ' ' : visibleLabel}</p>
      </div>

      {/* Filters: search, quick toggles, period, colour */}
      <div className="space-y-2 rounded-lg border border-border bg-surface p-2.5">
        <form
          className="flex flex-col gap-2 lg:flex-row lg:items-center"
          onSubmit={(e) => {
            e.preventDefault();
            run(query);
          }}
          role="search"
        >
          <label htmlFor="atlas-q" className="sr-only">Search trips</label>
          <input
            id="atlas-q"
            ref={searchRef}
            value={query}
            onChange={(e) => run(e.target.value, true)}
            aria-keyshortcuts="/"
            aria-describedby="atlas-keys"
            placeholder={locationId ? 'Search: long · q3 · runs · PRJ-2026-0176 · deal words · help' : 'Search: HCC · long · q3 2025 · runs · PRJ-2026-0176 · kickoff · help'}
            autoComplete="off"
            className="min-h-[36px] w-full rounded border border-border bg-bg px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-text lg:max-w-md"
          />
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Quick filters">
            {QUICK_FILTERS.map((f) => {
              const on = queryHasWord(query, f.word);
              return (
                <button
                  key={f.word}
                  type="button"
                  aria-pressed={on}
                  onClick={() => run(toggleQueryWord(query, f.word))}
                  className={`min-h-[30px] rounded-full px-2.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-text ${on ? 'bg-text font-semibold text-text-inverse' : 'bg-surface-3 text-text-muted hover:bg-surface-strong'}`}
                >
                  {f.label}
                </button>
              );
            })}
            {(query || selected || period.kind !== 'all') && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setSelected(null);
                  writeUrl({ q: null, trip: null, period: null });
                  frameIds([]);
                }}
                className="min-h-[30px] rounded-full px-2.5 text-xs text-text underline-offset-2 hover:underline"
              >
                Clear all
              </button>
            )}
          </div>
        </form>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <PeriodBar period={period} years={years} onChange={setPeriod} />
          <ColorByControl value={colorBy} onChange={setColor} />
        </div>
        <p className="min-h-[1rem] text-xs text-text-muted">{result.intent}</p>
      </div>

      {error ? (
        <div role="alert" className="rounded-lg border border-border bg-surface p-6 text-sm text-text">
          Could not load the trips. Refresh to try again.
        </div>
      ) : loading ? (
        <div role="status" className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted">Loading the atlas…</div>
      ) : total === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">
          {locationId ? 'No trips recorded for this property yet.' : 'No trips recorded yet.'}
        </div>
      ) : (
        <>
          <SummaryCards s={summary} compact={!locationId} />

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_340px]">
            {/* Canvas + HUD layer */}
            <div ref={canvasRef} className={`relative overflow-hidden rounded-lg border border-border bg-bg ${locationId ? 'h-[420px] sm:h-[560px]' : 'h-[460px] sm:h-[620px]'}`}>
              <TripAtlas3D
                atlas={atlas}
                highlight={visible}
                selectedTripId={selected}
                calloutIds={calloutIds}
                fitNonce={frame.nonce}
                fitIds={frame.ids}
                onSelect={selectFromScene}
                onAnchors={onAnchors}
                hoverTripId={listHover}
                onLane={onLane}
                colorBy={colorBy}
              />

              <CalloutLayer atlas={atlas} selected={selected} onSelect={selectFromScene} sinkRef={anchorSink} widthRef={canvasRef} byYear={Boolean(locationId)} />

              {/* Legend for the colour mode, top right */}
              <div className="absolute right-3 top-3 hidden w-[250px] space-y-1.5 border border-border bg-surface p-3 text-[11px] text-text md:block">
                <ColorLegend mode={colorBy} rows={legendRows} isOn={(w) => queryHasWord(query, w)} onToggle={(w) => run(toggleQueryWord(query, w))} />
                <p className="flex items-center gap-2 pt-1">
                  <span aria-hidden="true" className="shrink-0 px-1 text-[10px] font-semibold text-text">5 days</span>
                  <span>Label over every trip of {LONG_TRIP_DAYS}+ days</span>
                </p>
                <p className="flex items-center gap-2">
                  <span aria-hidden="true" className="inline-block h-0.5 w-6 shrink-0 bg-success" />
                  <span>Run: stops in order (dashed = not all done)</span>
                </p>
                <p className="flex items-center gap-2">
                  <span aria-hidden="true" className="relative inline-block h-3 w-6 shrink-0">
                    <span className="absolute bottom-0 left-2.5 h-2.5 w-px bg-warning" />
                    <span className="absolute left-[7px] top-0 h-1.5 w-1.5 rounded-full bg-warning" />
                  </span>
                  <span>Open item at the property</span>
                </p>
              </div>

              {/* Title block, bottom left */}
              <div className="pointer-events-none absolute bottom-3 left-3 max-w-[60%]">
                <p className="text-lg font-bold leading-tight text-text sm:text-2xl">
                  {locationId ? `${summary.trips} trips · ${periodLabel(period)}` : `${summary.trips} trips · ${propertyRows.filter((p) => p.visibleCount > 0).length} properties`}
                </p>
                <p className="text-xs text-text-muted">
                  {atlas.rangeLabel} · {atlas.runs.length} run{atlas.runs.length === 1 ? '' : 's'}
                </p>
              </div>

              {/* Provenance, bottom right — dated, like every PazNet drawing */}
              <p className="pointer-events-none absolute bottom-3 right-3 hidden max-w-[300px] text-right text-[10px] leading-snug text-text-muted sm:block">
                Sources: PazPortal trips and runs; trips, amounts and costs read-only from linked Zoho CRM deals.
                {asOf ? ` Data as of ${asOf.toISOString().slice(0, 16).replace('T', ' ')} UTC.` : ''}
              </p>

              <button
                type="button"
                onClick={() => frameIds(selected && !visible ? [selected] : visible ? [...visible] : [])}
                aria-keyshortcuts="f"
                className="absolute left-3 top-3 rounded border border-border bg-surface px-2.5 py-1 text-xs text-text hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-text"
              >
                Fit view
              </button>
            </div>

            {/* Panel: selection, the period breakdown, then the list */}
            <aside className={`space-y-3 rounded-lg border border-border bg-surface p-3 lg:overflow-y-auto ${locationId ? 'lg:max-h-[560px]' : 'lg:max-h-[620px]'}`}>
              {sel && selBlock ? (
                <section aria-labelledby="atlas-sel" className="space-y-2 border-b border-divider pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs text-text-muted">Selected trip</p>
                      <h2 id="atlas-sel" className="flex items-center gap-2 font-mono text-base font-semibold text-text">
                        {sel.project_number} <LongTag days={selBlock.days} />
                      </h2>
                    </div>
                    <button type="button" onClick={() => select(null)} aria-label="Clear selection" className="min-h-[32px] min-w-[32px] rounded text-text-muted hover:bg-surface-strong">×</button>
                  </div>
                  <p className="flex items-center gap-2 text-xs"><Swatch state={selBlock.state} /> {TRIP_STATES[selBlock.state].label}</p>
                  <dl className="grid grid-cols-[88px_1fr] gap-x-2 gap-y-1 text-sm">
                    {!locationId && (
                      <>
                        <dt className="text-xs text-text-muted">Property</dt>
                        <dd className="text-text">
                          <Link to={`/trips/atlas/${sel.location_id}`} className="underline-offset-2 hover:underline">{sel.location_name}</Link>
                        </dd>
                      </>
                    )}
                    <dt className="text-xs text-text-muted">Dates</dt>
                    <dd className="text-text">
                      {formatDate(sel.start_date)}
                      {sel.end_date && sel.end_date !== sel.start_date ? ` → ${formatDate(sel.end_date)}` : ''} · {selBlock.days} day{selBlock.days === 1 ? '' : 's'}
                    </dd>
                    {sel.scope && (
                      <>
                        <dt className="text-xs text-text-muted">Scope</dt>
                        <dd className="break-words text-text">{sel.scope}</dd>
                      </>
                    )}
                    <dt className="text-xs text-text-muted">Revenue</dt>
                    <dd className="tabular-nums text-text">
                      {!sel.finance ? 'No Zoho deal linked' : sel.finance.revenue === null ? 'No amount on the deal' : money(sel.finance.revenue)}
                    </dd>
                    {sel.finance && (
                      <>
                        <dt className="text-xs text-text-muted">Cost</dt>
                        <dd className="tabular-nums text-text">
                          {sel.finance.cost !== null ? money(sel.finance.cost) : sel.finance.readFromZoho ? 'Not recorded in Zoho' : 'Not read from Zoho yet'}
                        </dd>
                        {sel.finance.cost !== null && sel.finance.revenue !== null && (
                          <>
                            <dt className="text-xs text-text-muted">Profit</dt>
                            <dd className="tabular-nums text-text">
                              {money(sel.finance.revenue - sel.finance.cost)}
                              {tripMargin(sel.finance) !== null ? ` · ${pct(tripMargin(sel.finance)!)}` : ''}
                            </dd>
                          </>
                        )}
                      </>
                    )}
                    {selRun && (
                      <>
                        <dt className="text-xs text-text-muted">Run</dt>
                        <dd className="text-text">
                          <Link to={`/runs?run=${selRun.run_id}`} className="underline-offset-2 hover:underline">{selRun.run_number}</Link> · stop {selBlock.runStop} of {selRun.stop_count}
                        </dd>
                      </>
                    )}
                    {sel.invoice_reference && (
                      <>
                        <dt className="text-xs text-text-muted">Invoice</dt>
                        <dd className="font-mono text-text">{sel.invoice_reference}</dd>
                      </>
                    )}
                    {selDeals.length > 0 && (
                      <>
                        <dt className="text-xs text-text-muted">Zoho deal{selDeals.length === 1 ? '' : 's'}</dt>
                        <dd className="space-y-0.5 text-text">
                          {selDeals.map((d) =>
                            d.url ? (
                              <a key={d.externalId} href={d.url} target="_blank" rel="noreferrer" className="block break-words text-xs underline-offset-2 hover:underline">
                                {d.label ?? d.externalId} ↗
                              </a>
                            ) : (
                              <span key={d.externalId} className="block break-words text-xs">{d.label ?? d.externalId}</span>
                            ),
                          )}
                        </dd>
                      </>
                    )}
                    {openHere > 0 && (
                      <>
                        <dt className="text-xs text-text-muted">Open items</dt>
                        <dd className="text-warning">{openHere} at this property</dd>
                      </>
                    )}
                  </dl>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Link to={`/trips?trip=${sel.trip_id}`} className="rounded bg-text px-3 py-1.5 text-xs font-medium text-text-inverse hover:opacity-90">Open trip</Link>
                    {!locationId && (
                      <Link to={`/trips/atlas/${sel.location_id}`} className="rounded bg-surface-3 px-3 py-1.5 text-xs font-medium text-text hover:bg-surface-strong">
                        Property atlas
                      </Link>
                    )}
                  </div>
                </section>
              ) : null}

              <Breakdown title={breakdown.title} rows={breakdown.rows} activeKey={breakdown.active} onPick={setPeriod} />

              {!locationId && (
                <section aria-labelledby="atlas-props" className="space-y-1">
                  <h2 id="atlas-props" className="flex items-baseline justify-between text-xs font-semibold text-text">
                    <span>Properties — open one</span>
                    <span className="font-normal text-text-muted">visible trips · revenue</span>
                  </h2>
                  <ul className="divide-y divide-divider">
                    {propertyRows.filter((l) => !filtered || l.visibleCount > 0).map((l) => (
                      <li key={l.locationId}>
                        <Link
                          to={`/trips/atlas/${l.locationId}${colorParam ? `?color=${colorParam}` : ''}`}
                          className="flex w-full items-center justify-between gap-2 py-1.5 text-left text-xs hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-text"
                        >
                          <span className="min-w-0 flex-1 truncate text-text">{l.name}</span>
                          {l.openItems > 0 && <span className="text-warning">{l.openItems} open</span>}
                          {l.longTrips > 0 && <span className="text-info">{l.longTrips} long</span>}
                          <span className="tabular-nums text-text-muted">{l.visibleCount}</span>
                          <span className="w-[64px] text-right tabular-nums text-text">{money(l.revenue)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(locationId || filtered) && (
                <section aria-labelledby="atlas-list" className="space-y-1">
                  <h2 id="atlas-list" className="text-xs font-semibold text-text">{`${hits.length} trip${hits.length === 1 ? '' : 's'}`}</h2>
                  {hits.length === 0 ? (
                    <p className="text-xs text-text-muted">No trips match. Type "help" for what the search understands.</p>
                  ) : (
                    <ul className="divide-y divide-divider">
                      {hits.slice(0, 300).map((t) => {
                        const b = atlas.blockById.get(t.trip_id)!;
                        const isSel = t.trip_id === selected;
                        return (
                          <li
                            key={t.trip_id}
                            ref={(el) => {
                              if (el) rowRefs.current.set(t.trip_id, el);
                              else rowRefs.current.delete(t.trip_id);
                            }}
                          >
                            <button
                              type="button"
                              aria-current={isSel ? 'true' : undefined}
                              onClick={() => select(t.trip_id, true)}
                              onMouseEnter={() => setListHover(t.trip_id)}
                              onMouseLeave={() => setListHover((h) => (h === t.trip_id ? null : h))}
                              onFocus={() => setListHover(t.trip_id)}
                              onBlur={() => setListHover((h) => (h === t.trip_id ? null : h))}
                              className={`flex w-full items-start gap-2 border-l-2 py-1.5 pl-1.5 text-left text-xs hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-text ${isSel ? 'border-text bg-surface-strong' : 'border-transparent'}`}
                            >
                              <Swatch state={b.state} />
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-1.5 text-text">
                                  <span className="truncate">{locationId ? `${t.start_date}${b.days > 1 ? ` · ${b.days} days` : ''}` : t.location_name}</span>
                                  <LongTag days={b.days} />
                                </span>
                                <span className="block text-text-muted">
                                  {locationId ? '' : `${t.start_date} · `}
                                  <span className="font-mono">{t.project_number}</span>
                                </span>
                              </span>
                              <span className="shrink-0 tabular-nums text-text-muted">{t.finance?.revenue != null ? money(t.finance.revenue) : '—'}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              )}
            </aside>
            <p id="atlas-keys" className="text-[11px] text-text-faint lg:col-span-2">
              Drag to pan · scroll to zoom · {locationId ? 'click a year to show only that year' : 'click a property to open its atlas'} ·{' '}
              <kbd className="font-mono">/</kbd> search · <kbd className="font-mono">↑</kbd>
              <kbd className="font-mono">↓</kbd> step through the list · <kbd className="font-mono">←</kbd>
              <kbd className="font-mono">→</kbd> previous or next visit to the same property · <kbd className="font-mono">F</kbd> fit ·{' '}
              <kbd className="font-mono">Esc</kbd> clear
            </p>
          </div>
        </>
      )}
    </div>
  );
}
